import React, { HTMLAttributes, HTMLProps } from 'react';
import Immutable from 'immutable';
import { useSelector } from 'react-redux';

import {
  Taxonomy as TaxonomyTerm,
} from '@uncinc/react-drupal-core';
import { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import {
  WysiwygField,
} from '@uncinc/uncinc-react-kitchen-sink';

import useClasses from '../../../hooks/useClasses';
import { ImmutableMilestoneTaxonomyEntity, MilestoneTaxonomyProps } from '..';

import './style.scss';

interface Props extends HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableMilestoneTaxonomyEntity;
}

const TaxonomyMilestone: React.FC<Props> = ({ entity, className }) => {
  const { buildClass, joinClasses } = useClasses('TaxonomyMilestone');

  const name = entity.getIn(['name', 0, 'value'], '');
  const description = entity.getIn(['description', 0, 'processed'], '');
  const image = entity.getIn(['field_image', 0, 'entity', 'field_media_file', 0], Immutable.Map());
  const badges = entity.get('field_badges', Immutable.List());
  const badgeIds = badges.map((badge) => badge.getIn(['entity', 'tid', 0, 'value']));

  const userBadgeIdsOfMilestone = useSelector<ImmutableRootState<MilestoneTaxonomyProps['field_badges']>>((state) => {
    const uid = state.getIn(['login', 'uid']);
    const userBadges = state.getIn(['user', uid, 'field_badges']);

    return userBadges
      .map((badge) => badge.getIn(['entity', 'tid', 0, 'value']))
      .filter((id) => badgeIds.includes(id));
  }) as Immutable.List<number>;

  const percentageAchieved = Math.round((userBadgeIdsOfMilestone.size / badgeIds.size) * 100);

  return (
    <div className={joinClasses(buildClass(), className)}>
      <div className={buildClass('info')}>
        <div className={buildClass('image')}>
          <img src={image.get('url')} alt={image.get('description')} />
          <div
            className={buildClass('image-mask')}
            style={{ clipPath: `inset(0 0 ${percentageAchieved}% 0)` }}
          />
        </div>

        <div className={buildClass('content')}>
          <div className={buildClass('name')}>
            <h3 className="asH4">{name}</h3>
          </div>

          <div className={buildClass('description')}>
            <WysiwygField
              property="schema:text"
              html={description}
            />
          </div>
        </div>
      </div>

      {badges.size > 0 && (
        <ul className={buildClass('badges')}>
          {badges.map((badge) => {
            const id = badge.getIn(['entity', 'tid', 0, 'value']);
            const uuid: string = badge.getIn(['entity', 'uuid', 0, 'value']);
            const langcode: string = badge.getIn(['entity', 'langcode', 0, 'value']);

            return (
              <TaxonomyTerm
                key={`${uuid}:${langcode}`}
                id={id}
                entity={badge.get('entity')}
                achieved={userBadgeIdsOfMilestone.includes(id)}
              />
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default TaxonomyMilestone;
